import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Building2,
  Phone,
  AlertCircle,
  Loader2,
  Shield,
  ArrowLeft,
  Check,
  ChevronRight,
  FileText,
} from 'lucide-react';
import { useAuth, ACCOUNT_TYPES } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';

const Register = () => {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuth();
  const { t } = useTranslation();

  const [step, setStep] = useState(1);
  const [accountType, setAccountType] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    company: '',
    siret: '',
    position: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    clearError();
    setLocalError('');
  };

  const validateStep1 = () => {
    if (!accountType) {
      setLocalError(t('auth.selectAccountType'));
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.firstName.trim()) {
      setLocalError(t('auth.firstNameRequired'));
      return false;
    }
    if (!formData.lastName.trim()) {
      setLocalError(t('auth.lastNameRequired'));
      return false;
    }
    if (!formData.email.trim()) {
      setLocalError(t('auth.emailRequired'));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setLocalError(t('auth.invalidEmail'));
      return false;
    }
    if (accountType === ACCOUNT_TYPES.ENTERPRISE) {
      if (!formData.company.trim()) {
        setLocalError(t('auth.companyNameRequired'));
        return false;
      }
      if (!formData.siret.trim()) {
        setLocalError(t('auth.siretRequired'));
        return false;
      }
      if (!/^\d{14}$/.test(formData.siret.replace(/\s/g, ''))) {
        setLocalError(t('auth.siretInvalid'));
        return false;
      }
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.password) {
      setLocalError(t('auth.passwordRequired'));
      return false;
    }
    if (formData.password.length < 8) {
      setLocalError(t('auth.passwordMinLength'));
      return false;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      setLocalError(t('auth.passwordStrength'));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setLocalError(t('auth.passwordMismatch'));
      return false;
    }
    if (!acceptedTerms) {
      setLocalError(t('auth.termsRequired'));
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setLocalError('');
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    setLocalError('');
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep3()) return;

    const result = await register({
      ...formData,
      accountType,
    });

    if (result.success) {
      navigate('/login', { state: { registered: true } });
    }
  };

  const passwordStrength = () => {
    const password = formData.password;
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;
    return strength;
  };

  const getStrengthLabel = () => {
    const strength = passwordStrength();
    if (strength <= 2) return { label: t('auth.weak'), color: 'bg-red-500' };
    if (strength <= 3) return { label: t('auth.medium'), color: 'bg-amber-500' };
    if (strength <= 4) return { label: t('auth.strong'), color: 'bg-green-500' };
    return { label: t('auth.veryStrong'), color: 'bg-emerald-500' };
  };

  return (
    <div className="min-h-screen flex">
      {}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-blue-700 to-blue-800 p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">Covenant Tracker</span>
          </div>

          <div className="max-w-md">
            <h1 className="text-4xl font-bold text-white mb-6">
              {t('auth.joinThousands')}
            </h1>
            <p className="text-blue-100 text-lg mb-12">
              {t('auth.startManaging')}
            </p>
          </div>

          {}
          <div className="space-y-4">
            {[
              { num: 1, title: t('auth.accountType'), desc: t('auth.accountTypeDesc') },
              { num: 2, title: t('auth.yourInfo'), desc: t('auth.yourInfoDesc') },
              { num: 3, title: t('auth.security'), desc: t('auth.securityDesc') },
            ].map((s) => (
              <div
                key={s.num}
                className={`flex items-center gap-4 p-4 rounded-lg transition-colors ${
                  step === s.num
                    ? 'bg-white/20'
                    : step > s.num
                    ? 'bg-white/10'
                    : 'bg-transparent'
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step > s.num
                      ? 'bg-white text-blue-600'
                      : step === s.num
                      ? 'bg-white text-blue-600'
                      : 'bg-white/20 text-white'
                  }`}
                >
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <div>
                  <p className="text-white font-semibold">{s.title}</p>
                  <p className="text-blue-200 text-sm">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-blue-200 text-sm">
          © 2025 Covenant Tracker. {t('auth.allRightsReserved')}
        </div>
      </div>

      {}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-lg">
          {}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800">Covenant Tracker</span>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            {}
            {step > 1 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-800 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('common.back')}
              </button>
            )}

            {}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                {step === 1 && t('auth.createAccount')}
                {step === 2 && t('auth.yourInformation')}
                {step === 3 && t('auth.secureAccount')}
              </h2>
              <p className="text-slate-500">
                {step === 1 && t('auth.chooseAccountType')}
                {step === 2 &&
                  (accountType === ACCOUNT_TYPES.ENTERPRISE
                    ? t('auth.companyInfo')
                    : t('auth.personalInfo'))}
                {step === 3 && t('auth.createSecurePassword')}
              </p>
            </div>

            {}
            <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-2 rounded-full transition-all ${
                    step >= s ? 'w-8 bg-blue-600' : 'w-2 bg-slate-200'
                  }`}
                />
              ))}
            </div>

            {}
            {(error || localError) && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error || localError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {}
              {step === 1 && (
                <div className="space-y-4">
                  <button
                    type="button"
                    onClick={() => setAccountType(ACCOUNT_TYPES.ENTERPRISE)}
                    className={`w-full p-6 border-2 rounded-xl text-left transition-all ${
                      accountType === ACCOUNT_TYPES.ENTERPRISE
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          accountType === ACCOUNT_TYPES.ENTERPRISE
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        <Building2 className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-slate-800">{t('auth.enterprise')}</h3>
                          {accountType === ACCOUNT_TYPES.ENTERPRISE && (
                            <Check className="w-5 h-5 text-blue-500" />
                          )}
                        </div>
                        <p className="text-slate-500 text-sm mt-1">
                          {t('auth.enterpriseDesc')}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                            {t('auth.multiUsers')}
                          </span>
                          <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                            {t('auth.teamManagement')}
                          </span>
                          <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                            {t('auth.advancedReports')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setAccountType(ACCOUNT_TYPES.INDIVIDUAL)}
                    className={`w-full p-6 border-2 rounded-xl text-left transition-all ${
                      accountType === ACCOUNT_TYPES.INDIVIDUAL
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          accountType === ACCOUNT_TYPES.INDIVIDUAL
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        <User className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-slate-800">{t('auth.individual')}</h3>
                          {accountType === ACCOUNT_TYPES.INDIVIDUAL && (
                            <Check className="w-5 h-5 text-blue-500" />
                          )}
                        </div>
                        <p className="text-slate-500 text-sm mt-1">
                          {t('auth.individualDesc')}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                            {t('auth.personalAccount')}
                          </span>
                          <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                            {t('auth.fullAccess')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={handleNext}
                    className="w-full mt-6 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {t('auth.continue')}
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}

              {}
              {step === 2 && (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('profile.firstName')} *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="Jean"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('profile.lastName')} *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Dupont"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('auth.email')} *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="vous@exemple.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('profile.phone')}
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>
                  </div>

                  {accountType === ACCOUNT_TYPES.ENTERPRISE && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('profile.company')} *
                        </label>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder="Nom de votre entreprise"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('auth.siretNumber')} *
                        </label>
                        <div className="relative">
                          <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="text"
                            name="siret"
                            value={formData.siret}
                            onChange={handleChange}
                            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder="123 456 789 00012"
                            maxLength={17}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('auth.position')}
                        </label>
                        <input
                          type="text"
                          name="position"
                          value={formData.position}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="Directeur financier"
                        />
                      </div>
                    </>
                  )}

                  <button
                    type="button"
                    onClick={handleNext}
                    className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {t('auth.continue')}
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}

              {}
              {step === 3 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('auth.password')} *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full pl-11 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>

                    {}
                    {formData.password && (
                      <div className="mt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${getStrengthLabel().color}`}
                              style={{ width: `${(passwordStrength() / 5) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500 w-16">
                            {getStrengthLabel().label}
                          </span>
                        </div>
                        <ul className="text-xs text-slate-500 space-y-1">
                          <li className={formData.password.length >= 8 ? 'text-green-600' : ''}>
                            {formData.password.length >= 8 ? '✓' : '○'} {t('auth.atLeast8Chars')}
                          </li>
                          <li className={/[A-Z]/.test(formData.password) ? 'text-green-600' : ''}>
                            {/[A-Z]/.test(formData.password) ? '✓' : '○'} {t('auth.oneUppercase')}
                          </li>
                          <li className={/[a-z]/.test(formData.password) ? 'text-green-600' : ''}>
                            {/[a-z]/.test(formData.password) ? '✓' : '○'} {t('auth.oneLowercase')}
                          </li>
                          <li className={/\d/.test(formData.password) ? 'text-green-600' : ''}>
                            {/\d/.test(formData.password) ? '✓' : '○'} {t('auth.oneDigit')}
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('auth.confirmPassword')} *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className={`w-full pl-11 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                          formData.confirmPassword &&
                          formData.password !== formData.confirmPassword
                            ? 'border-red-300'
                            : 'border-slate-300'
                        }`}
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {formData.confirmPassword &&
                      formData.password !== formData.confirmPassword && (
                        <p className="mt-1 text-sm text-red-500">
                          {t('auth.passwordMismatch')}
                        </p>
                      )}
                  </div>

                  {}
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="w-4 h-4 mt-1 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-600">
                      {t('auth.acceptTerms')}{' '}
                      <a href="#" className="text-blue-600 hover:underline">
                        {t('auth.termsOfUse')}
                      </a>{' '}
                      {t('auth.and')}{' '}
                      <a href="#" className="text-blue-600 hover:underline">
                        {t('auth.privacyPolicy')}
                      </a>
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {t('auth.creatingAccount')}
                      </>
                    ) : (
                      t('auth.registerButton')
                    )}
                  </button>
                </div>
              )}
            </form>

            {}
            <p className="text-center text-slate-600 mt-6">
              {t('auth.hasAccount')}{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                {t('auth.loginButton')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
